/**
 * AdminRevenuePage — Umsatzauswertung ueber alle Zahlungsplattformen.
 *
 * Loest den Legacy-Screen /admin/premium_payments ab (v1-Paritaet). Sieben
 * Kennzahlen-Kacheln: fuenf rollierende Zeitraeume mit exakt den Definitionen
 * der alten App (damit die Zahlen vergleichbar bleiben) und zwei kalenderbasierte
 * fuer die Buchhaltungssicht. Darunter der gestapelte Tagesverlauf.
 *
 * Alle Betraege in USD — siehe Kommentar in useAdminRevenue.
 *
 * Zur Wurzel-Klasse: NICHT `className="page"` verwenden. Die traegt die AppShell
 * bereits (layout.css: Grid mit 252px-Sidebar-Spalte); verschachtelt kollabiert
 * dadurch der gesamte Seiteninhalt. Siehe die ausfuehrliche Notiz in
 * AdminUsersPage.tsx.
 *
 * Kein eigener Admin-Guard: die Seite haengt unter der Layout-Route
 * routes/_auth.admin.tsx, die das zentral erledigt.
 */
import { useTranslation } from 'react-i18next'
import { CalendarDays, CalendarRange, Clock, Sun, Sunrise, TrendingUp } from 'lucide-react'
import {
  useAdminRevenue,
  REVENUE_BUCKET_KEYS,
  type AdminRevenueBucket,
  type AdminRevenueBucketKey,
} from './useAdminRevenue'
import { RevenueByProviderChart } from './RevenueByProviderChart'
import { providerColor, fmtUsd } from './revenueFormat'

/**
 * Darstellung je Zeitraum. Die Toenung ist rein kosmetisch; das Icon
 * unterscheidet rollierende (Uhr/Sonne) von kalenderbasierten (Kalender) Bloecken.
 */
const BUCKET_PRESENTATION: Record<
  AdminRevenueBucketKey,
  { tone: string; icon: typeof Clock }
> = {
  today: { tone: 'kpi--brand', icon: Sun },
  yesterday: { tone: '', icon: Sunrise },
  last_24h: { tone: 'kpi--accent', icon: Clock },
  last_7_days: { tone: 'kpi--teal', icon: TrendingUp },
  last_30_days: { tone: 'kpi--success', icon: TrendingUp },
  calendar_week: { tone: 'kpi--warning', icon: CalendarRange },
  calendar_month: { tone: 'kpi--warning', icon: CalendarDays },
}

/**
 * Aufteilung in zwei Reihen 4 + 3. Eine reine Trennung nach rollierend (5) und
 * kalenderbasiert (2) haette die erste Reihe im vierspaltigen Grid 4+1
 * umbrechen lassen — es gibt keine .grid-5-Klasse, und eine neue anzulegen
 * waere fuer eine Kachelreihe zu viel. Welcher Zeitraum wie rechnet, steht
 * ohnehin im Beschriftungstext und in der Fussnote.
 */
const FIRST_ROW_KEYS = REVENUE_BUCKET_KEYS.slice(0, 4) as readonly AdminRevenueBucketKey[]
const SECOND_ROW_KEYS = REVENUE_BUCKET_KEYS.slice(4) as readonly AdminRevenueBucketKey[]

type TileProps = {
  bucketKey: AdminRevenueBucketKey
  bucket: AdminRevenueBucket
}

function RevenueTile({ bucketKey, bucket }: TileProps) {
  const { t } = useTranslation('adminRevenue')
  const { tone, icon: Icon } = BUCKET_PRESENTATION[bucketKey]

  // Groesste Plattform zuerst — bei sechs Zahlungswegen ist die Reihenfolge
  // sonst willkuerlich und die Kachel schwer zu lesen.
  const providers = Object.entries(bucket.providers).sort((a, b) => b[1].total - a[1].total)

  return (
    <div className={`kpi ${tone}`} role="listitem" data-testid={`revenue-tile-${bucketKey}`}>
      <div className="kpi-label">
        <span className="kpi-icon">
          <Icon aria-hidden="true" size={15} />
        </span>
        {t(`periods.${bucketKey}`)}
      </div>
      <div className="kpi-value tnum">{fmtUsd(bucket.total)}</div>
      <div className="kpi-sub">{t('kpi.transactions', { count: bucket.count })}</div>

      {providers.length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {providers.map(([provider, value]) => (
            <div
              key={provider}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: providerColor(provider),
                  flexShrink: 0,
                }}
              />
              <span style={{ color: 'var(--text-muted)' }}>
                {t(`providers.${provider}`, { defaultValue: provider.toUpperCase() })}
              </span>
              <span className="tnum" style={{ marginLeft: 'auto', color: 'var(--text)' }}>
                {fmtUsd(value.total)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function AdminRevenuePage() {
  const { t } = useTranslation('adminRevenue')
  const { revenue, isLoading, isError, refetch } = useAdminRevenue()

  return (
    <div className="admin-revenue-page" data-testid="admin-revenue-page">
      <div className="page-header">
        <div>
          <h1>{t('page.title')}</h1>
          <p className="muted" style={{ marginTop: 4 }}>
            {t('page.subtitle')}
          </p>
        </div>
      </div>

      {isLoading && (
        <div aria-busy="true" aria-label={t('loading')} data-testid="admin-revenue-loading">
          {t('loading')}
        </div>
      )}

      {isError && (
        <div className="error-state" role="alert" data-testid="admin-revenue-error">
          <p>{t('error.load')}</p>
          <button type="button" className="btn btn-primary" onClick={() => refetch()}>
            {t('error.retry')}
          </button>
        </div>
      )}

      {!isLoading && !isError && revenue && (
        <>
          <div className="grid grid-4" role="list" data-testid="revenue-tiles-primary">
            {FIRST_ROW_KEYS.map((key) => (
              <RevenueTile key={key} bucketKey={key} bucket={revenue.buckets[key]} />
            ))}
          </div>

          <div
            className="grid grid-3"
            role="list"
            style={{ marginTop: 16 }}
            data-testid="revenue-tiles-secondary"
          >
            {SECOND_ROW_KEYS.map((key) => (
              <RevenueTile key={key} bucketKey={key} bucket={revenue.buckets[key]} />
            ))}
          </div>

          <p className="muted" style={{ marginTop: 12, fontSize: 12 }}>
            {t('currencyNote', { timeZone: revenue.time_zone })}
          </p>

          <div style={{ marginTop: 16 }}>
            <RevenueByProviderChart series={revenue.series} />
          </div>
        </>
      )}
    </div>
  )
}
